/* The read-out's building blocks. TEMPLATE-OWNED.
 *
 * readout.js (yours) composes these into what the page shows; nothing here knows what
 * the business sells. Every helper returns DOM nodes or null, and null is dropped, so a
 * value the call has not established simply does not appear.
 */

export const num = (v) => (v === null || v === undefined ? null : String(Number(v.toFixed(2))));
export const money = (v, locale = "en-AU") =>
  v === null || v === undefined ? null : `$${Number(v).toLocaleString(locale)}`;
export const pretty = (v) => (v ? String(v).replace(/_/g, " ") : null);

// Past about this length a value can no longer sit opposite its label in half a column
// without turning into a ragged right-aligned paragraph; it goes underneath.
const STACK_AT = 34;
// A third of the panel is a narrow column, so a value stacks under its label much sooner
// in the finished sheet than it does in the live read-out.
const SHEET_STACK_AT = 20;

export function rowsFrom(pairs) {
  return pairs
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([label, value, strong]) => {
      const row = document.createElement("div");
      row.className = String(value).length > STACK_AT ? "row row-stack" : "row";
      const l = document.createElement("span");
      l.className = "row-label";
      l.textContent = label;
      const v = document.createElement("span");
      v.className = strong ? "row-value row-value-strong" : "row-value";
      v.textContent = value;
      row.append(l, v);
      return row;
    });
}

export function group(title, rows, wide) {
  if (!rows.length) return null;
  const wrap = document.createElement("section");
  wrap.className = wide ? "readout-group readout-group-wide" : "readout-group";
  const h = document.createElement("h3");
  h.className = "group-title";
  h.textContent = title;
  wrap.append(h, ...rows);
  return wrap;
}

export function alertRow(text, quiet) {
  const alert = document.createElement("p");
  alert.className = quiet ? "alert-row alert-row-quiet" : "alert-row";
  alert.textContent = text;
  return alert;
}

export function renderStepper(node, steps, current) {
  const at = steps.findIndex(([key]) => key === current);
  node.replaceChildren(
    ...steps.map(([, label], i) => {
      const li = document.createElement("li");
      li.className = `step${i < at ? " step-done" : ""}${i === at ? " step-now" : ""}`;
      li.textContent = label;
      return li;
    })
  );
}

export function sheetRow(label, value, strong) {
  if (value === null || value === undefined || value === "") return null;
  const row = document.createElement("div");
  row.className = String(value).length > SHEET_STACK_AT ? "s-row s-row-stack" : "s-row";
  const l = document.createElement("span");
  l.className = "s-label";
  l.textContent = label;
  const val = document.createElement("span");
  val.className = strong ? "s-value s-value-strong" : "s-value";
  val.textContent = value;
  row.append(l, val);
  return row;
}

/** One column, made of [title, nodes] blocks; empty blocks disappear entirely. */
export function sheetColumn(blocks) {
  const kept = [];
  for (const [title, nodes] of blocks) {
    const rows = nodes.filter(Boolean);
    if (!rows.length) continue;
    const h = document.createElement("h4");
    h.className = kept.length ? "sheet-title sheet-title-gap" : "sheet-title";
    h.textContent = title;
    kept.push(h, ...rows);
  }
  if (!kept.length) return null;
  const col = document.createElement("div");
  col.className = "sheet-sec";
  col.append(...kept);
  return col;
}

export function bulletList(lines) {
  const list = document.createElement("ul");
  list.className = "s-list";
  for (const line of lines) {
    const li = document.createElement("li");
    li.textContent = line;
    list.append(li);
  }
  return list;
}

/** The demo's opening suggestions, from readout.js's OPENERS. */
export function renderOpeners(node, openers) {
  if (!node) return;
  node.replaceChildren(
    ...openers.map((text) => {
      const li = document.createElement("li");
      li.className = "opener";
      li.textContent = text;
      return li;
    })
  );
  // An empty list would otherwise leave "Try opening with" hanging over nothing.
  const wrap = node.closest(".openers");
  if (wrap) wrap.hidden = openers.length === 0;
}
